import {GetServerSideProps, InferGetServerSidePropsType} from 'next';
import {useRouter} from 'next/router';
import React, {Fragment, useCallback, useMemo} from 'react';

import Col from '../../features/common/Col';
import {useEnvironment} from '../../features/common/EnvironmentProvider';
import Row from '../../features/common/Row';
import {useBreakpoint} from '../../features/common/util/useBreakpoint';
import {UserRole} from '../../features/query/graphql';
import useCurrentUser from '../../features/query/useCurrentUser';
import {getCmsEntry, getCmsList} from '../../features/strapi';
import {
  ApiCategoryCategory,
  ApiFooterFooter,
  ApiResourceResource,
  ApiResourcesPageResourcesPage,
} from '../../features/strapi/generated/contentTypes';
import {ProcessedValues, StrapiPagination} from '../../features/strapi/types';
import Button from '../../features/tool/components/buttons/Button';
import Footer from '../../features/tool/components/Footer';
import {getToolLayout} from '../../features/tool/components/Layout';
import Newsletter from '../../features/tool/components/Newsletter';
import Pagination from '../../features/tool/components/Pagination';
import ResourceCard from '../../features/tool/components/ResourceCard';
import ResourcesNavigation from '../../features/tool/components/ResourcesNavigation';
import ResourcesSearch from '../../features/tool/components/ResourcesSearch';
import SimpleCard from '../../features/tool/components/SimpleCard';
import {NoResultsIcon} from '../../resources/Icons';
import URLS, {getResourcesURL} from '../../urls';
import {NextPageWithLayout} from '../_app';

type ResourcesQuery = {
  page: string;
  search: string;
  filters: string;
};

type ServerSideProps = {
  resourcesPage: ProcessedValues<ApiResourcesPageResourcesPage>;
  categories: ProcessedValues<ApiCategoryCategory>[];
  resources: ProcessedValues<ApiResourceResource>[];
  footer: ProcessedValues<ApiFooterFooter>;
  _pagination: StrapiPagination | null;
};

const FIRST_PAGE_SMALL_BOX_IDX = [2, 3, 6, 7, 8];
const SMALL_BOX_IDX = [2, 3, 4];

const RESOURCES_PAGE_ELEMENTS = 9;

const ResourcesPage: NextPageWithLayout<InferGetServerSidePropsType<typeof getServerSideProps>> = ({
  resourcesPage,
  categories,
  resources,
  _pagination,
  footer,
}) => {
  const environment = useEnvironment();
  const {isMobile} = useBreakpoint('mobile');
  const user = useCurrentUser();
  const router = useRouter();
  const searchQuery = router.query.search as string;
  const filterQuery = router.query.filters as string;
  const selectedFilters = useMemo(() => filterQuery?.split(',') || [], [filterQuery]);
  const goToResourcesPage = useCallback(() => router.replace(getResourcesURL(), undefined), [router]);
  const handleSearchValueChange = useCallback(
    (searchValue: string) => router.push(getResourcesURL(1, searchValue, selectedFilters)),
    [router, selectedFilters]
  );
  const handleFilterToggled = useCallback(
    (slug: string) => {
      const filterQuery = selectedFilters.includes(slug)
        ? selectedFilters.filter(q => q !== slug)
        : [...selectedFilters, slug];
      router.push(getResourcesURL(1, searchQuery, filterQuery));
    },
    [router, searchQuery, selectedFilters]
  );

  return (
    <>
      <ResourcesNavigation active={'resources'} />
      <div className={'bg-blue-100'}>
        <div className="w-full max-w-limit m-auto px-sm py-24 flex flex-row mobile:flex-col mobile:gap-y-12 justify-between items-center">
          <Col className="space-y-4">
            <div className="text-primary-600 font-semibold text-[1.6rem]">RESOURCES</div>
            <div className="text-huge font-semibold leading-tight">{searchQuery ?? resourcesPage.title}</div>
          </Col>
          {user?.role === UserRole.Editor && (
            <Button variant="primary" href={URLS.draft}>
              <Row className="items-center gap-x-5">
                <div>View draft resources</div>
                <div className="h-4 w-4 rounded-full bg-blue-400" />
              </Row>
            </Button>
          )}
        </div>
      </div>
      <div className="w-full max-w-limit m-auto -mt-9 px-sm">
        <ResourcesSearch
          searchValue={searchQuery}
          filters={categories.map(c => ({
            slug: c.slug,
            label: c.name,
            toggled: selectedFilters.includes(c.slug),
          }))}
          placeholder="Search"
          onSearchValueChange={handleSearchValueChange}
          onFilterToggled={handleFilterToggled}
        />
      </div>
      {!isMobile && (!!searchQuery || !!filterQuery) && resources.length > 0 && (
        <div className="w-full max-w-limit text-xs mx-auto px-sm text-gray-200 text-right mb-4">
          {resources.length} results
        </div>
      )}
      <div className="w-full mx-auto max-w-limit px-sm grid grid-cols-6 mobile:grid-cols-1 gap-16 grow">
        {resources.map((resource, idx) => {
          const smallBoxIdx = _pagination?.page === 1 && !searchQuery ? FIRST_PAGE_SMALL_BOX_IDX : SMALL_BOX_IDX;
          const colSpan = isMobile ? 'col-span-1' : smallBoxIdx.includes(idx) ? 'col-span-2' : 'col-span-3';
          if (!searchQuery && _pagination?.page === 1 && idx === 2) {
            return (
              <Fragment key={`resource-card-${idx}`}>
                <SimpleCard
                  className={colSpan}
                  title={resourcesPage.play_title}
                  subtitle={resourcesPage.play_subtitle}
                  href={URLS.game}
                />
                <ResourceCard className={colSpan} data={resource} />
              </Fragment>
            );
          } else {
            return <ResourceCard key={`resource-card-${idx}`} className={colSpan} data={resource} />;
          }
        })}
      </div>
      {resources.length === 0 && (
        <Col className="items-center text-center gap-y-[1.2rem] max-w-[22rem] mx-auto my-[15rem]">
          <NoResultsIcon className="w-24 h-24" />
          <div className="font-semibold">No results found</div>
          <div className="leading-tight text-sm">We couldn&apos;t find anything that matches your search.</div>
          <Button className="mt-6" variant="secondary" accessory="back" onClick={goToResourcesPage}>
            Back to index
          </Button>
        </Col>
      )}
      {_pagination && (
        <Pagination
          className={'my-24'}
          page={_pagination.page}
          pageCount={_pagination.pageCount}
          getPageUrl={page => getResourcesURL(page, searchQuery, selectedFilters)}
        />
      )}{' '}
      {environment && <Newsletter data={environment.newsletter} />}
      <Footer data={footer} />
    </>
  );
};

export const getServerSideProps: GetServerSideProps<ServerSideProps> = async context => {
  const {page, search, filters} = context.query as ResourcesQuery;

  if (isNaN(parseInt(page))) {
    return {notFound: true};
  }

  const {resourcesPage} = await getCmsEntry<ApiResourcesPageResourcesPage, 'resourcesPage'>({
    path: 'resources-page',
    params: {populate: '*'},
    rootKey: 'resourcesPage',
  });
  const {resources, _pagination} = await getCmsList<ApiResourceResource, 'resources'>({
    path: 'resources',
    params: {
      populate: ['category', 'image', 'author'],
      pagination: {page: Number(page), pageSize: RESOURCES_PAGE_ELEMENTS},
      sort: ['id:desc'],
      filters: {
        $or: [
          {
            title: {
              $containsi: search,
            },
          },
          {
            body: {
              $containsi: search,
            },
          },
        ],
        ...(filters && {
          category: {
            slug: {
              $in: filters.split(','),
            },
          },
        }),
      },
    },
    processDataMap: {image: 'image', category: 'commonChild', author: 'commonChild', date: 'date'},
    pagination: true,
  });
  const {footer} = await getCmsEntry<ApiFooterFooter, 'footer'>({path: 'footer'});
  const {categories} = await getCmsList<ApiCategoryCategory, 'categories'>({path: 'categories'});
  return {
    props: {
      resourcesPage,
      resources,
      _pagination,
      footer,
      categories,
    },
  };
};

export default ResourcesPage;

ResourcesPage.getLayout = getToolLayout;

ResourcesPage.title = 'Resources';
